import React, { useState, useEffect } from 'react';
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { 
  setGameSoundsMuted, 
  setGameSoundsVolume, 
  setBackgroundMusicMuted, 
  setBackgroundMusicVolume,
  playGameSound
} from '@/utils/sound-utils';
import appSound from '@/services/app-sound-service';
import { Volume2, VolumeX, Music, Square } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { EnhancedHeader } from '@/components/ui/enhanced-header';
import { AppLayout } from '@/components/ui/app-layout';

export function SoundManager() {
  // State for sound settings
  const [sfxVolume, setSfxVolume] = useState(0.5);
  const [musicVolume, setMusicVolume] = useState(0.3);
  const [sfxMuted, setSfxMuted] = useState(false);
  const [musicMuted, setMusicMuted] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const storedSfxVolume = localStorage.getItem('sound-effects-volume');
    const storedMusicVolume = localStorage.getItem('background-music-volume');
    const storedSfxMuted = localStorage.getItem('sound-effects-muted');
    const storedMusicMuted = localStorage.getItem('background-music-muted');
    
    if (storedSfxVolume) setSfxVolume(parseFloat(storedSfxVolume));
    if (storedMusicVolume) setMusicVolume(parseFloat(storedMusicVolume));
    if (storedSfxMuted) setSfxMuted(storedSfxMuted === 'true');
    if (storedMusicMuted) setMusicMuted(storedMusicMuted === 'true');
  }, []);

  // Handle sound effects volume change
  const handleSfxVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setSfxVolume(newVolume);
    setGameSoundsVolume(newVolume);
    localStorage.setItem('sound-effects-volume', newVolume.toString());
    
    // Play a sample sound to preview the new volume
    if (!sfxMuted) {
      playGameSound('ui.button');
    }
  };

  // Handle music volume change
  const handleMusicVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setMusicVolume(newVolume);
    setBackgroundMusicVolume(newVolume);
    localStorage.setItem('background-music-volume', newVolume.toString());
  };

  // Handle sound effects mute toggle
  const handleSfxMuteChange = (checked: boolean) => {
    setSfxMuted(checked);
    setGameSoundsMuted(checked);
    localStorage.setItem('sound-effects-muted', checked.toString());
    
    // Play a sound when unmuting to confirm it works
    if (!checked) {
      setTimeout(() => playGameSound('ui.button'), 300);
    }
  };

  // Handle music mute toggle
  const handleMusicMuteChange = (checked: boolean) => {
    setMusicMuted(checked);
    setBackgroundMusicMuted(checked);
    localStorage.setItem('background-music-muted', checked.toString());
    
    // If unmuting and no music is playing, start the menu music
    if (!checked && !appSound.currentBackgroundMusic) {
      setTimeout(() => appSound.playMenuBackgroundMusic(), 300);
    }
  };

  // Play a test sound
  const playTestSound = () => {
    appSound.playUISound('success');
  };

  // Stop all background music
  const stopMusic = () => {
    appSound.stopBackgroundMusic();
  };

  // Play menu background music
  const playMenuMusic = () => {
    appSound.playMenuBackgroundMusic();
  };

  // Play game background music
  const playGameMusic = () => {
    appSound.playGameBackgroundMusic();
  };

  return (
    <AppLayout
      title="Sound Settings"
      backPath="/settings"
    >
      <div className="container py-6">
        <div className="space-y-6">
          {/* Sound Effects Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Volume2 className="h-5 w-5 text-primary" />
                Sound Effects
              </CardTitle>
              <CardDescription>
                Control the volume of all game sound effects
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="sfx-volume" className="text-sm">Volume</Label>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(sfxVolume * 100)}%
                  </span>
                </div>
                <Slider
                  id="sfx-volume"
                  defaultValue={[sfxVolume]}
                  max={1}
                  step={0.01}
                  value={[sfxVolume]}
                  onValueChange={handleSfxVolumeChange}
                  disabled={sfxMuted}
                  className={sfxMuted ? "opacity-50" : ""}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <VolumeX className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sfx-muted">Mute Sound Effects</Label>
                </div>
                <Switch
                  id="sfx-muted"
                  checked={sfxMuted}
                  onCheckedChange={handleSfxMuteChange}
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button size="sm" onClick={playTestSound}>
                Play Test Sound
              </Button>
            </CardFooter>
          </Card>

          {/* Background Music Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="h-5 w-5 text-primary" />
                Background Music
              </CardTitle>
              <CardDescription>
                Control the volume of background music
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="music-volume" className="text-sm">Volume</Label>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(musicVolume * 100)}%
                  </span>
                </div>
                <Slider
                  id="music-volume"
                  defaultValue={[musicVolume]}
                  max={1}
                  step={0.01}
                  value={[musicVolume]}
                  onValueChange={handleMusicVolumeChange}
                  disabled={musicMuted}
                  className={musicMuted ? "opacity-50" : ""}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Music className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="music-muted">Mute Background Music</Label>
                </div>
                <Switch
                  id="music-muted"
                  checked={musicMuted}
                  onCheckedChange={handleMusicMuteChange}
                />
              </div>
            </CardContent>
            <CardFooter className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={playMenuMusic}>
                Play Menu Music
              </Button>
              <Button size="sm" onClick={playGameMusic}>
                Play Game Music
              </Button>
              <Button size="sm" variant="outline" onClick={stopMusic}>
                <Square className="h-4 w-4 mr-2" />
                Stop Music
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
